import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import expressions from '../../../Service/expressions';
import PropTypes from 'prop-types';

const VisibilityContext = createContext({
  isLoading: true,
  visibilities: {},
  hasError: false,
});

const getVisibilityKey = (paneId, groupId, pluginId) => {
  return `CommandPalette.${paneId}.${groupId}.${pluginId}`;
};

const getVisibilityExpressionsFromPlugins = (paneId, groupId, plugins) => {
  return Object.fromEntries(
    plugins.map((plugin) => [
      getVisibilityKey(paneId, groupId, plugin.id),
      plugin.visExp,
    ]),
  );
};

const getVisibilityExpressionsFromGroups = (paneId, groups) => {
  return groups.reduce((acc, group) => {
    return {
      ...acc,
      ...getVisibilityExpressionsFromPlugins(
        paneId,
        group.groupId,
        group.plugins,
      ),
    };
  }, {});
};

const getVisibilityExpressionsFromPanes = (panes) => {
  return panes.reduce((acc, pane) => {
    return {
      ...acc,
      ...getVisibilityExpressionsFromGroups(pane.paneId, pane.groups),
    };
  }, {});
};

const resolveVisibilityExpressions = async (visibilityExpressions) => {
  return expressions.evaluate(visibilityExpressions, {
    contextType: 'CommandPalette',
  });
};

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {CommandPalettePane[]} props.panes
 */
export const VisibilityProvider = ({ children, panes }) => {
  const [visibilities, setVisibilities] = useState(undefined);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const evaluateVisibility = async () => {
      const visibilities = getVisibilityExpressionsFromPanes(panes);
      const resolvedVisibilities =
        await resolveVisibilityExpressions(visibilities);

      if (resolvedVisibilities.hasError) {
        setHasError(true);
      } else {
        setVisibilities(resolvedVisibilities);
      }

      setIsLoaded(true);
    };

    evaluateVisibility();
  }, [panes]);

  return (
    <VisibilityContext.Provider value={{ visibilities, isLoaded, hasError }}>
      {children}
    </VisibilityContext.Provider>
  );
};

export const useVisibilityContext = (paneId, groupId) => {
  const { visibilities, isLoaded, hasError } = useContext(VisibilityContext);

  return {
    isLoaded,
    hasError,
    getVisibility: useCallback(
      (pluginId) => {
        return isLoaded && visibilities && !visibilities.hasError
          ? visibilities[getVisibilityKey(paneId, groupId, pluginId)]
          : false;
      },
      [isLoaded, visibilities, paneId, groupId],
    ),
  };
};

VisibilityProvider.propTypes = {
  children: PropTypes.node.isRequired,
  panes: PropTypes.array.isRequired,
};
